import { useState, useEffect, useCallback } from 'react';
import { api } from '@expat-ui/shared';
import { formatCurrencyInput, extractNumericInput, formatAmount } from '../../utils/numberFormat';
import { getFreshAccessToken } from '../../utils/authUtils';
import { useResidencyCountries } from '../../hooks/useApi';
import { Goal, GoalCategory, Country, GOAL_CATEGORY_LABELS, COUNTRY_CURRENCIES } from '../../types/api.types';
import styles from './GoalsPage.module.scss';

const GOAL_CATEGORIES: GoalCategory[] = ['REAL_ESTATE', 'EDUCATION', 'WEDDING', 'TRAVEL', 'VEHICLE', 'OTHER'];

const COUNTRY_DISPLAY: Record<Country, string> = { INDIA: 'India', US: 'USA', CAN: 'Canada' };

export function GoalsPage() {
    const [goals, setGoals] = useState<Goal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

    const fetchGoals = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const data = await api.get<Goal[]>('/goals', token);
            setGoals(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load goals');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGoals();
    }, [fetchGoals]);

    const deleteGoal = async (goalId: string) => {
        const token = await getFreshAccessToken();
        if (!token) return;
        await api.delete(`/goals/${goalId}`, token);
        fetchGoals();
    };

    const openEdit = (goal: Goal) => setEditingGoal(goal);

    const closeModal = () => {
        setShowForm(false);
        setEditingGoal(null);
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading goals...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={fetchGoals}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Goals</h1>
                    <p className={styles.subtitle}>Define milestone goals like buying property or funding education.</p>
                </div>
                <button className={styles.addButton} onClick={() => setShowForm(true)}>
                    <span className="material-symbols-outlined">add_circle</span>
                    Add Goal
                </button>
            </div>

            <details className={styles.guide}>
                <summary className={styles.guideSummary}>
                    <div className={styles.guideSummaryLeft}>
                        <span className="material-symbols-outlined">lightbulb</span>
                        <span className={styles.guideSummaryText}>How goals work in the simulator</span>
                    </div>
                    <span className={`material-symbols-outlined ${styles.guideChevron}`}>expand_more</span>
                </summary>
                <div className={styles.guideBody}>
                    <ul className={styles.guideList}>
                        <li>
                            Each goal is withdrawn from your portfolio at your target age in every simulation — it is
                            real money out, not just a tracker.
                        </li>
                        <li>
                            The simulator shows a <strong>Goal Outcomes</strong> section with the % of scenarios where
                            your portfolio had enough to fund each goal.
                        </li>
                        <li>
                            If you cannot afford a goal, the simulator drains whatever remains and continues — a severe
                            shortfall can cascade into portfolio failure for that simulation.
                        </li>
                        <li>
                            <strong>Don't duplicate:</strong> if you've added the same event as a Retirement one-time
                            expense, remove one. Both are deducted independently.
                        </li>
                    </ul>
                </div>
            </details>

            <div className={styles.listSectionHeader}>
                <h2 className={styles.listSectionTitle}>Goal Breakdown</h2>
            </div>

            {goals.length === 0 ? (
                <button className={styles.addAnotherBtn} onClick={() => setShowForm(true)}>
                    <span className="material-symbols-outlined">add_circle</span>
                    <span>Add your first goal</span>
                </button>
            ) : (
                <>
                    <div className={styles.goalList}>
                        {goals.map((goal) => (
                            <GoalCard key={goal.goalId} goal={goal} onDelete={deleteGoal} onEdit={openEdit} />
                        ))}
                    </div>
                    <button className={styles.addAnotherBtn} onClick={() => setShowForm(true)}>
                        <span className="material-symbols-outlined">add_circle</span>
                        <span>Add another goal</span>
                    </button>
                </>
            )}

            {(showForm || editingGoal) && (
                <GoalFormModal
                    initialGoal={editingGoal ?? undefined}
                    onClose={closeModal}
                    onSaved={() => {
                        closeModal();
                        fetchGoals();
                    }}
                />
            )}
        </div>
    );
}

function GoalCard({
    goal,
    onDelete,
    onEdit,
}: {
    goal: Goal;
    onDelete: (id: string) => void;
    onEdit: (goal: Goal) => void;
}) {
    const countryLabel = COUNTRY_DISPLAY[goal.country] ?? goal.country;

    const cardAccentClass =
        goal.country === 'INDIA' ? styles.cardIndia : goal.country === 'US' ? styles.cardUS : styles.cardCAN;

    return (
        <div className={`${styles.card} ${cardAccentClass}`}>
            <div className={styles.cardInner}>
                <div className={styles.cardLeft}>
                    <div className={styles.cardLabelRow}>
                        <h3 className={styles.cardLabel}>{goal.label}</h3>
                        <span className={styles.cardCategoryBadge}>{GOAL_CATEGORY_LABELS[goal.category]}</span>
                        <span className={styles.cardCountryBadge}>{countryLabel}</span>
                    </div>
                    <div className={styles.cardTimingRow}>
                        <div className={styles.cardTimingItem}>
                            <span className="material-symbols-outlined">event</span>
                            Target Age: {goal.targetAge}
                        </div>
                    </div>
                </div>
                <div className={styles.cardRight}>
                    <div className={styles.cardAmountBlock}>
                        <p className={styles.cardAmount}>{formatAmount(goal.targetAmount, goal.currency)}</p>
                        <p className={styles.cardFrequency}>Target</p>
                    </div>
                    <div className={styles.cardIconActions}>
                        <button className={styles.iconBtnEdit} onClick={() => onEdit(goal)} title="Edit">
                            <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button className={styles.iconBtnDelete} onClick={() => onDelete(goal.goalId)} title="Delete">
                            <span className="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface GoalFormModalProps {
    initialGoal?: Goal;
    onClose: () => void;
    onSaved: () => void;
}

function GoalFormModal({ initialGoal, onClose, onSaved }: GoalFormModalProps) {
    const isEditing = !!initialGoal;
    const { countryOptions, countries } = useResidencyCountries();
    const [label, setLabel] = useState(initialGoal?.label ?? '');
    const [category, setCategory] = useState<GoalCategory>(initialGoal?.category ?? 'REAL_ESTATE');
    const [country, setCountry] = useState<Country>(initialGoal?.country ?? countries[0] ?? 'INDIA');
    const currency = COUNTRY_CURRENCIES[country];
    const [targetAmount, setTargetAmount] = useState(initialGoal ? String(initialGoal.targetAmount) : '');
    const [targetAge, setTargetAge] = useState(initialGoal ? String(initialGoal.targetAge) : '50');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const payload = {
                label,
                category,
                country,
                targetAmount: Number(targetAmount),
                currency,
                targetAge: Number(targetAge),
            };
            if (isEditing) {
                await api.put(`/goals/${initialGoal.goalId}`, payload, token);
            } else {
                await api.post('/goals', payload, token);
            }
            onSaved();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>{isEditing ? 'Edit Goal' : 'Add Goal'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Goal Name</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g., Buy apartment in Hyderabad"
                            required
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as GoalCategory)}
                        >
                            {GOAL_CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                    {GOAL_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {countryOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Target Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(targetAmount, currency)}
                                onChange={(e) => setTargetAmount(extractNumericInput(e.target.value))}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Target Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={targetAge}
                                onChange={(e) => setTargetAge(e.target.value)}
                                min={25}
                                max={95}
                                required
                            />
                        </div>
                    </div>

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Goal'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
